<template>
  <section
    class="pdf-download-module-component"
    :class="{
      'invert-background': invertBackground,
      'remove-padding-top': removePaddingTop,
      'remove-padding-bottom': removePaddingBottom,
    }">
    <div class="inner-container">
      <div class="pdf-download-inner">
        <div class="pdf-download-card">
          <div
            class="pdf-download-copy"
            v-html="props.data?.text"></div>

          <div
            v-if="fileUrl"
            class="pdf-download-actions">
            <img
              v-if="showPreviewImage"
              class="pdf-download-thumbnail"
              :src="props.data.image.url"
              :alt="props.data.image.alt || documentTitle"
              :style="previewImageStyle"
              loading="lazy" />

            <a
              ref="previewTrigger"
              class="pdf-download-icon-button no-router"
              :href="fileUrl"
              :target="showPreviewInModal ? null : '_blank'"
              :rel="showPreviewInModal ? null : 'noopener noreferrer'"
              :aria-label="`Preview ${documentTitle}`"
              @click="handlePreviewClick">
              <svg
                aria-hidden="true"
                viewBox="0 0 24 24">
                <path
                  d="M7 3.75h7.25L18 7.5v5.25M14 3.75V7.5h4M5.75 3.75H5A1.25 1.25 0 0 0 3.75 5v14A1.25 1.25 0 0 0 5 20.25h7.25" />
                <circle
                  cx="16.25"
                  cy="16.25"
                  r="3.25" />
                <path d="m18.7 18.7 2.05 2.05" />
              </svg>
            </a>

            <a
              class="pdf-download-icon-button no-router"
              :href="fileUrl"
              :download="fileName"
              :aria-label="`Download ${documentTitle}`">
              <svg
                aria-hidden="true"
                viewBox="0 0 24 24">
                <path d="M12 3.5v14M6.75 12.25 12 17.5l5.25-5.25" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </div>

    <Teleport to="body">
      <Transition name="pdf-preview-fade">
        <div
          v-if="previewOpen"
          class="pdf-preview-modal"
          role="dialog"
          aria-modal="true"
          :aria-label="`Preview ${documentTitle}`"
          @click.self="closePreview">
          <div
            data-lenis-prevent
            class="pdf-preview-modal-content">
            <button
              ref="modalClose"
              class="pdf-preview-modal-close"
              type="button"
              aria-label="Close preview"
              @click="closePreview">
              <span></span>
              <span></span>
            </button>

            <div class="pdf-preview-modal-viewer">
              <iframe
                :src="fileUrl"
                :title="documentTitle"></iframe>
            </div>

            <div class="pdf-preview-modal-footer">
              <h2>{{ documentTitle }}</h2>
              <a
                class="pdf-preview-download"
                :href="fileUrl"
                :download="fileName">
                Download
                <svg
                  aria-hidden="true"
                  viewBox="0 0 16 16">
                  <path d="M8 2v8m0 0 3-3m-3 3L5 7M3 13h10" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </section>
</template>

<script setup>
/* #META
 {
  "NAME": "PDFDownloadComponent",
  "CLASSNAME": "pdf-download-module-component",
  "URL": "http://vuevitewp.local/test"
 }
#ENDMETA */

import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import useSmoothScroll from '@/components/composables/useSmoothScroll'

const props = defineProps(['data'])
const { locoScroll } = useSmoothScroll()
const previewOpen = ref(false)
const previewTrigger = ref(null)
const modalClose = ref(null)
let previousBodyOverflow = ''

const fileUrl = computed(() => props.data?.file?.url || '')

const fileName = computed(() => {
  if (props.data?.file?.filename) return props.data.file.filename
  if (!fileUrl.value) return ''

  return fileUrl.value.split('/').pop()?.split('?')[0] || ''
})

const plainText = (value) => {
  if (typeof document === 'undefined') {
    return String(value || '')
      .replace(/<[^>]*>/g, '')
      .trim()
  }

  const parser = document.createElement('div')
  parser.innerHTML = String(value || '')
  return parser.textContent?.trim() || ''
}

const documentTitle = computed(
  () => props.data?.file?.title || plainText(props.data?.text) || 'PDF document'
)

const showPreviewImage = computed(
  () =>
    ![false, 0, '0'].includes(props.data?.add_preview_image) &&
    props.data?.image?.url
)

const showPreviewInModal = computed(() =>
  [true, 1, '1'].includes(props.data?.show_preview_in_modal)
)

const invertBackground = computed(() =>
  [true, 1, '1'].includes(
    props.data?.invert_background ?? props.data?.invertBackground
  )
)

const removePaddingTop = computed(() =>
  [true, 1, '1'].includes(
    props.data?.remove_padding_top ?? props.data?.removePaddingTop
  )
)

const removePaddingBottom = computed(() =>
  [true, 1, '1'].includes(
    props.data?.remove_padding_bottom ?? props.data?.removePaddingBottom
  )
)

const pixelValue = (value) => {
  const number = Number.parseFloat(value)
  return Number.isFinite(number) && number > 0 ? `${number}px` : undefined
}

const previewImageStyle = computed(() => ({
  width: pixelValue(props.data?.width),
  height: pixelValue(props.data?.height),
}))

const openPreview = () => {
  previewOpen.value = true
  nextTick(() => modalClose.value?.focus())
}

const handlePreviewClick = (event) => {
  if (!showPreviewInModal.value) return

  event.preventDefault()
  openPreview()
}

const closePreview = () => {
  previewOpen.value = false
  nextTick(() => previewTrigger.value?.focus())
}

const handleKeydown = (event) => {
  if (event.key === 'Escape' && previewOpen.value) closePreview()
}

watch(previewOpen, (isOpen) => {
  if (isOpen) {
    previousBodyOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    locoScroll.value?.stop?.()
    return
  }

  document.body.style.overflow = previousBodyOverflow
  locoScroll.value?.start?.()
})

onMounted(() => window.addEventListener('keydown', handleKeydown))

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleKeydown)
  document.body.style.overflow = previousBodyOverflow
  locoScroll.value?.start?.()
})
</script>

<style lang="scss" scoped>
.pdf-download-module-component {
  @apply bg-white w-full py-8 lg:py-10;

  &.invert-background {
    @apply bg-functional;

    .pdf-download-inner .pdf-download-card {
      @apply bg-white;
    }
  }

  &.remove-padding-top {
    @apply pt-0 lg:pt-0;
  }

  &.remove-padding-bottom {
    @apply pb-0 lg:pb-0;
  }

  .pdf-download-inner {
    @apply w-full;

    .pdf-download-card {
      @apply flex w-full flex-col justify-between gap-8 rounded-[20px] bg-functional p-5
      md:flex-row md:items-center md:gap-[35px];

      .pdf-download-copy {
        @apply min-w-0 flex-1;

        :deep(h1),
        :deep(h2),
        :deep(h3),
        :deep(h4),
        :deep(h5),
        :deep(h6) {
          @apply header-4 text-balanced text-center lg:text-left;
        }

        :deep(p) {
          @apply text-[18px] leading-[24px] text-balanced;

          &:last-child {
            @apply mb-0;
          }
        }
      }

      .pdf-download-actions {
        @apply flex shrink-0 items-center justify-center gap-4;

        .pdf-download-thumbnail {
          @apply mr-1 lg:mr-6 shrink-0 object-contain;
        }

        .pdf-download-icon-button {
          @apply flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-full border-0
          bg-balanced p-0 text-progressive no-underline transition-colors hover:bg-humble
          focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-balanced;

          svg {
            @apply h-6 w-6;

            path,
            circle {
              fill: none;
              stroke: white;
              stroke-linecap: round;
              stroke-linejoin: round;
              stroke-width: 1.6;
            }
          }
        }
      }
    }
  }
}

.pdf-preview-fade {
  &-enter-active,
  &-leave-active {
    @apply transition-opacity duration-200;
  }

  &-enter-from,
  &-leave-to {
    @apply opacity-0;
  }
}

.pdf-preview-modal {
  @apply fixed inset-0 z-[101] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm md:p-8;

  .pdf-preview-modal-content {
    @apply relative flex max-h-full w-full max-w-[1100px] flex-col overflow-hidden rounded-[20px] bg-white shadow-2xl;

    .pdf-preview-modal-close {
      @apply absolute right-3 top-3 z-2 flex h-11 w-11 items-center justify-center rounded-full border
      border-balanced bg-white transition-colors hover:bg-balanced;

      span {
        @apply absolute h-[1px] w-5 bg-balanced transition-colors;

        &:first-child {
          @apply rotate-45;
        }

        &:last-child {
          @apply -rotate-45;
        }
      }

      &:hover span {
        @apply bg-white;
      }
    }

    .pdf-preview-modal-viewer {
      @apply flex min-h-[260px] flex-1 items-center justify-center overflow-auto bg-[#dfe3e2] p-4 md:min-h-[520px];

      iframe {
        @apply h-[70vh] min-h-[420px] w-full rounded-[10px] border-0 bg-white;

        @media (max-width: 639px) {
          min-height: 55vh;
        }
      }
    }

    .pdf-preview-modal-footer {
      @apply flex flex-col gap-4 px-5 py-5 pr-16 sm:flex-row sm:items-center sm:justify-between md:px-8;

      h2 {
        @apply mb-0 text-[20px] font-medium leading-tight text-balanced md:text-[24px];
      }

      .pdf-preview-download {
        @apply inline-flex items-center justify-center gap-[10px] rounded-full bg-progressive px-4 py-1.5
        text-[16px] leading-tight text-humble no-underline transition-colors hover:bg-balanced hover:text-white;

        svg {
          @apply h-4 w-4;

          path {
            fill: none;
            stroke: currentColor;
            stroke-linecap: round;
            stroke-linejoin: round;
            stroke-width: 1.4;
          }
        }
      }
    }
  }
}
</style>
